/**
 * Mixpanel Event Spec v1 constants for FRISKY webapp
 * Matches iOS schema for cross-platform analytics parity
 */

export const SCHEMA_VERSION = 1
export const HEARTBEAT_INTERVAL_SECONDS = 900
export const JOURNAL_PERSIST_THROTTLE_MS = 5000
export const POST_PURCHASE_WINDOW_SECONDS = 600
export const CORRELATION_ID_EXPIRY_MS = 30 * 60 * 1000

// Event names - matches iOS for cross-platform dashboards
export const EVENTS = {
    // App Lifecycle
    APP_LOAD_STARTED: 'app_load_started',
    APP_READY: 'app_ready',

    // Playback
    PLAYBACK_REQUESTED: 'playback_requested',
    AUDIO_STARTED: 'audio_started',
    PLAYBACK_HEARTBEAT: 'playback_heartbeat',
    PLAYBACK_ENDED: 'playback_ended',
    PLAYBACK_ERROR: 'playback_error',

    // Premium Wall
    PREMIUM_WALL_SHOWN: 'premium_wall_shown',
    PREMIUM_WALL_CTA_TAPPED: 'premium_wall_cta_tapped',
    PREMIUM_WALL_DISMISSED: 'premium_wall_dismissed',

    // Premium Upsell Banner (web-only; no iOS equivalent)
    PREMIUM_BANNER_SEEN: 'premium_banner_seen',
    PREMIUM_BANNER_TAPPED: 'premium_banner_tapped',

    // External Players settings (web-only; no iOS equivalent)
    EXTERNAL_PLAYER_PAGE_VIEWED: 'external_player_page_viewed',
    EXTERNAL_PLAYER_PLAYLIST_DOWNLOADED: 'external_player_playlist_downloaded',
    EXTERNAL_PLAYER_URL_COPIED: 'external_player_url_copied',
    EXTERNAL_PLAYER_KEY_REGENERATED: 'external_player_key_regenerated',

    // Purchase & Entitlement
    PURCHASE_STARTED: 'purchase_started',
    PURCHASE_SUCCEEDED: 'purchase_succeeded',
    ENTITLEMENT_SYNC_STARTED: 'entitlement_sync_started',
    ENTITLEMENT_SYNC_RESULT: 'entitlement_sync_result',

    // Engagement
    MIX_FAVORITED: 'mix_favorited',
    MIX_UNFAVORITED: 'mix_unfavorited'
}

// Property keys - matches iOS
export const PROPS = {
    // App Lifecycle
    APP_LOAD_ID: 'app_load_id',
    LAUNCH_TYPE: 'launch_type',
    START_REASON: 'start_reason',
    TTI_MS: 'tti_ms',
    TT_APP_READY_MS: 'tt_app_ready_ms',
    READY_STATE: 'ready_state',

    // Playback
    PLAYBACK_REQUEST_ID: 'playback_request_id',
    CONTENT_TYPE: 'content_type',
    CONTENT_ID: 'content_id',
    SOURCE_SCREEN: 'source_screen',
    TT_AUDIO_STARTED_MS: 'tt_audio_started_ms',
    DELTA_LISTENING_SECONDS: 'delta_listening_seconds',
    LISTENING_SECONDS_CUMULATIVE: 'listening_seconds_cumulative',
    PLAYHEAD_SEC: 'playhead_sec',
    IS_BACKGROUND: 'is_background',
    PLAYER_STATE: 'player_state',
    ATTRIBUTION_SOURCE: 'attribution_source',
    ENDED_REASON: 'ended_reason',
    ERROR_STAGE: 'error_stage',
    ERROR_CODE: 'error_code',
    ERROR_MESSAGE: 'error_message',
    RADIO_CHANNEL_NAME: 'radio_channel_name',

    // Premium Wall
    PAYWALL_INSTANCE_ID: 'paywall_instance_id',
    WALL_TYPE: 'wall_type',
    PRESENTATION: 'presentation',
    TRIGGER_REASON: 'trigger_reason',
    ENTRY_POINT: 'entry_point',
    CTA: 'cta',
    DISMISS_METHOD: 'dismiss_method',

    // Premium Upsell Banner (web-only)
    BANNER_IMPRESSION_ID: 'banner_impression_id',
    BANNER_SURFACE: 'banner_surface',

    // External Players settings (web-only)
    IS_PREMIUM: 'is_premium',
    STREAM_QUALITY: 'stream_quality',
    PLAYLIST_FORMAT: 'playlist_format',

    // Purchase & Entitlement
    PURCHASE_ATTEMPT_ID: 'purchase_attempt_id',
    PRODUCT_ID: 'product_id',
    TRANSACTION_ID: 'transaction_id',
    RESULT: 'result',
    TIME_SINCE_PURCHASE_MS: 'time_since_purchase_ms',
    BILLING_PERIOD: 'billing_period',
    IS_TRIAL: 'is_trial',
    IS_RETURNING: 'is_returning',

    // Super Properties
    PLATFORM: 'platform',
    ENVIRONMENT: 'environment',
    SCHEMA_VERSION: 'schema_version',
    APP_VERSION: 'app_version',
    BUILD_NUMBER: 'build_number',
    IS_LOGGED_IN: 'is_logged_in',
    MEMBER_ID: 'member_id',

    // Engagement
    MIX_ID: 'mix_id'
}

export const CONTENT_TYPES = {
    RADIO: 'radio',
    ON_DEMAND: 'on_demand',
    DOWNLOADED_MIX: 'downloaded_mix'
}

export const ENDED_REASONS = {
    USER_STOP: 'user_stop',
    TRACK_END: 'track_end',
    SWITCH_CONTENT: 'switch_content',
    ERROR: 'error',
    APP_BACKGROUND: 'app_background',
    RECOVERED: 'recovered'
}

export const PLAYER_STATES = {
    PLAYING: 'playing',
    PAUSED: 'paused',
    BUFFERING: 'buffering',
    STALLED: 'stalled'
}

export const ERROR_STAGES = {
    REQUEST: 'request',
    LOAD: 'load',
    START: 'start',
    MIDSTREAM: 'midstream'
}

export const WALL_TYPES = {
    STREAMING_BLOCKED: 'streaming_blocked',
    RESTART_PREMIUM: 'restart_premium',
    AUDIO_QUALITY_BLOCKED: 'audio_quality_blocked',
    PREMIUM_CARD: 'premium_card',
    PREMIUM_WELCOME: 'premium_welcome',
    VIEW_PLANS: 'view_plans'
}

export const PRESENTATION_TYPES = {
    PAGE: 'page',
    MODAL: 'modal'
}

export const TRIGGER_REASONS = {
    PLAYBACK_BLOCKED: 'playback_blocked',
    USER_INITIATED: 'user_initiated',
    QUALITY_BLOCKED: 'quality_blocked',
    UNKNOWN: 'unknown'
}

export const DISMISS_METHODS = {
    CLOSE: 'close',
    BACKDROP: 'backdrop',
    ESCAPE: 'escape',
    BACK_NAVIGATION: 'back_navigation'
}

export const CTA_TYPES = {
    UPGRADE: 'upgrade',
    START_TRIAL: 'start_trial',
    VIEW_PLANS: 'view_plans',
    RESTORE: 'restore'
}

// Attribution sources - REQUIRED on all playback events (use UNKNOWN as fallback)
export const ATTRIBUTION_SOURCES = {
    DIRECT: 'direct',
    SEARCH: 'search',
    RECOMMENDATION: 'recommendation',
    LIBRARY: 'library',
    DEEPLINK: 'deeplink',
    AUTO_ADVANCE: 'auto_advance',
    UNKNOWN: 'unknown'
}

export const LAUNCH_TYPES = {
    NAVIGATE: 'navigate',
    RELOAD: 'reload',
    BACK_FORWARD: 'back_forward'
}

// Start reasons - why the app load was initiated (web equivalents of iOS values)
export const START_REASONS = {
    COLD_START: 'cold_start',       // Fresh navigation (typed URL, bookmark)
    DEEPLINK: 'deeplink',           // URL with specific content route
    REFRESH: 'refresh',             // Page reload
    BACK_FORWARD: 'back_forward',   // Browser history navigation
    UNKNOWN: 'unknown'              // Fallback
}

export const ENTITLEMENT_RESULTS = {
    SUCCESS: 'success',
    TIMEOUT: 'timeout',
    FAILED: 'failed'
}

// Billing periods — exact values match iOS BillingPeriod (AnalyticsSpec.swift)
export const BILLING_PERIODS = {
    MONTHLY: 'monthly',
    ANNUAL: 'annual'
}

// USER_STATUS removed - use PROPS.IS_LOGGED_IN with boolean values (true/false)

export const SOURCE_SCREENS = {
    RADIO_TAB: 'radio_tab',
    RADIO_PLAYER: 'radio_player',
    MIX_DETAIL: 'mix_detail',
    BROWSE_CHARTS: 'browse_charts',
    BROWSE_GENRES: 'browse_genres',
    SEARCH: 'search',
    LIBRARY_FAVORITES: 'library_favorites',
    ACCOUNT: 'account',
    SETTINGS: 'settings',
    MINI_PLAYER: 'mini_player',
    FULL_PLAYER: 'full_player',
    HOME: 'home'
}

export const ENTRY_POINTS = {
    TOP_BAR: 'top_bar',
    SIDEBAR: 'sidebar',
    TAB_BAR: 'tab_bar',
    FOOTER: 'footer',
    ACCOUNT_MENU: 'account_menu',
    SETTINGS_AUDIO_QUALITY: 'settings_audio_quality',
    MIX_PLAY_BLOCKED: 'mix_play_blocked',
    RADIO_PLAY_BLOCKED: 'radio_play_blocked',
    DIRECT_NAVIGATION: 'direct_navigation',
    PREMIUM_UPSELL_MODAL: 'premium_upsell_modal',
    HOME_BANNER: 'home_banner',
    RADIO_BANNER: 'radio_banner',
    RADIO_CHANNEL_BANNER: 'radio_channel_banner',
    SETTINGS_EXTERNAL_PLAYERS: 'settings_external_players'
}

// Premium upsell banner surfaces (web-only funnel dimension)
export const BANNER_SURFACES = {
    HOME: 'home',
    RADIO: 'radio',
    RADIO_CHANNEL: 'radio_channel',
    SETTINGS_EXTERNAL_PLAYERS: 'settings_external_players',
    UNKNOWN: 'unknown'
}

// Banner `source` strings ('home-banner' | 'radio-banner' | 'radio-channel-<id>')
// are frozen by the shipped banner feature; this derives the funnel dimensions.
export function bannerAttributionFromSource(source) {
    if (source === 'home-banner') return { surface: BANNER_SURFACES.HOME, channel: null }
    if (source === 'radio-banner') return { surface: BANNER_SURFACES.RADIO, channel: null }
    if (typeof source === 'string' && source.startsWith('radio-channel-')) {
        return { surface: BANNER_SURFACES.RADIO_CHANNEL, channel: source.slice('radio-channel-'.length) }
    }
    if (source === 'settings-external-players') {
        return { surface: BANNER_SURFACES.SETTINGS_EXTERNAL_PLAYERS, channel: null }
    }
    return { surface: BANNER_SURFACES.UNKNOWN, channel: null }
}

export const STORAGE_KEYS = {
    PAYWALL_INSTANCE: 'mp_paywall_instance',
    PURCHASE_ATTEMPT: 'mp_purchase_attempt',
    PURCHASE_TIMESTAMP: 'mp_purchase_timestamp',
    PLAYBACK_JOURNAL: 'mp_playback_journal',
    PURCHASE_PLAN: 'mp_purchase_plan',
    BANNER_ATTRIBUTION: 'mp_banner_attribution'
}

// Webapp plans are monthly (durationMonths 1) or annual (12); >= 12 => annual.
// Mirrors iOS: subscriptionPeriod.unit == .year ? .annual : .monthly
export function billingPeriodFromDurationMonths(durationMonths) {
    return Number(durationMonths) >= 12 ? BILLING_PERIODS.ANNUAL : BILLING_PERIODS.MONTHLY
}
